import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { Button } from '../../components/ui/Button';
import { InputField } from '../../components/ui/InputField';
import { Modal } from '../../components/ui/Modal';
import { api, ApiError } from '../../lib/api';
import { setTokens } from '../../lib/tokens';
import type { WorkspaceSummary } from '../../lib/types';

const WORKSPACE_LIMIT = 2;

function initialsOf(name: string): string {
  const words = name.replace(/[«»"]/g, '').split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '??';
}

export function WorkspaceChoicePage() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [continuing, setContinuing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const atLimit = workspaces.length >= WORKSPACE_LIMIT;

  const loadWorkspaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listWorkspaces();
      setWorkspaces(list);
      setSelected((prev) => prev ?? list[0]?.id ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить рабочие пространства.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteWorkspace(deleteTarget.id);
      setDeleteTarget(null);
      if (selected === deleteTarget.id) setSelected(null);
      await loadWorkspaces();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить рабочее пространство.');
    } finally {
      setDeleting(false);
    }
  };

  const handleContinue = async () => {
    if (!selected) return;
    setError(null);
    setContinuing(true);
    try {
      const response = await api.selectWorkspace(selected);
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      navigate('/app');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось выбрать рабочее пространство.');
    } finally {
      setContinuing(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mx-auto flex w-full max-w-[566px] flex-col gap-6">
        <div>
          <p className="text-[10px] font-semibold tracking-wide text-ai-accent">
            ШАГ 2 ИЗ 2 · РАБОЧЕЕ ПРОСТРАНСТВО
          </p>
          <h1 className="mt-3 text-[28px] font-semibold text-text-primary">Куда вы хотите войти?</h1>
          <p className="mt-2 text-[13px] text-text-secondary">
            Выберите организацию или создайте новое пространство.
          </p>
        </div>

        {loading && <p className="text-sm text-text-secondary">Загрузка…</p>}

        {!loading && (
          <div className="flex flex-col gap-4">
            {workspaces.map((ws) => {
              const isSelected = selected === ws.id;
              return (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => setSelected(ws.id)}
                  className={`flex items-center gap-4 rounded-[14px] border p-4 text-left ${
                    isSelected
                      ? 'border-2 border-action-primary bg-bg-surface-secondary'
                      : 'border-border-default bg-bg-surface'
                  }`}
                >
                  <span
                    className={`flex size-12 shrink-0 items-center justify-center rounded-xl text-xs font-semibold ${
                      isSelected
                        ? 'bg-action-primary text-text-inverse'
                        : 'bg-bg-surface-secondary text-text-secondary'
                    }`}
                  >
                    {initialsOf(ws.name)}
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-text-primary">{ws.name}</span>
                    <span className="mt-1 block text-[10px] text-text-secondary">
                      {ws.role_codes.join(', ') || 'Без роли'}
                    </span>
                    <span
                      className={`mt-2 block text-[10px] font-medium ${
                        ws.member_status === 'active' ? 'text-status-success' : 'text-text-secondary'
                      }`}
                    >
                      {ws.member_status === 'active' ? '● Активный участник' : ws.member_status}
                    </span>
                  </span>
                  <span
                    className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold text-text-inverse ${
                      isSelected ? 'border-action-primary bg-action-primary' : 'border-border-strong'
                    }`}
                  >
                    {isSelected && '✓'}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    title="Удалить пространство"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(ws);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        setDeleteTarget(ws);
                      }
                    }}
                    className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-status-danger/10 hover:text-status-danger"
                  >
                    ✕
                  </span>
                </button>
              );
            })}

            {workspaces.length === 0 && (
              <p className="text-xs text-text-secondary">
                У вас пока нет рабочих пространств — создайте первое.
              </p>
            )}

            {atLimit ? (
              <div className="rounded-[14px] border border-dashed border-border-default bg-bg-surface-secondary p-4">
                <p className="text-[13px] font-semibold text-text-primary">
                  Достигнут лимит пространств ({workspaces.length}/{WORKSPACE_LIMIT})
                </p>
                <p className="mt-1 text-[11px] text-text-secondary">
                  Удалите одно из существующих пространств (✕ на карточке), чтобы создать новое.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="flex items-center gap-4 rounded-[14px] border border-dashed border-border-default bg-bg-surface p-4 text-left"
              >
                <span className="text-2xl font-normal text-action-primary">+</span>
                <span>
                  <span className="block text-[13px] font-semibold text-text-primary">
                    Создать новое рабочее пространство
                  </span>
                  <span className="mt-1 block text-[10px] text-text-secondary">
                    {workspaces.length}/{WORKSPACE_LIMIT} — для новой организации или отдельного проекта
                  </span>
                </span>
              </button>
            )}
          </div>
        )}

        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}

        <Button type="button" className="w-full" disabled={!selected || continuing} onClick={handleContinue}>
          {continuing ? 'Входим…' : 'Продолжить'}
        </Button>

        <div className="rounded-xl bg-bg-surface-secondary p-4">
          <p className="text-[11px] leading-relaxed text-text-secondary">
            <span className="font-semibold text-action-primary">i</span> Приглашение действует 7 дней.
            После входа администратор сможет изменить вашу роль и доступ к данным.
          </p>
        </div>
      </div>

      <CreateWorkspaceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (ws) => {
          setCreateOpen(false);
          await loadWorkspaces();
          setSelected(ws.id);
        }}
      />

      <Modal open={deleteTarget != null} onClose={() => setDeleteTarget(null)}>
        <h2 className="text-lg font-semibold text-text-primary">Удалить пространство?</h2>
        <p className="mt-2 text-sm text-text-secondary">
          «{deleteTarget?.name}» будет удалено безвозвратно вместе со всеми картами и слоями внутри него.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Нет
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleting ? 'Удаляем…' : 'Да, удалить'}
          </button>
        </div>
      </Modal>
    </AuthLayout>
  );
}

type CreateWorkspaceModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (ws: WorkspaceSummary) => void;
};

function CreateWorkspaceModal({ open, onClose, onCreated }: CreateWorkspaceModalProps) {
  const [organizationName, setOrganizationName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const ws = await api.createWorkspace({
        organization_name: organizationName,
        workspace_name: workspaceName,
      });
      setOrganizationName('');
      setWorkspaceName('');
      onCreated(ws);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать рабочее пространство.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-text-primary">Новое рабочее пространство</h2>
        <InputField
          label="Название организации"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          required
        />
        <InputField
          label="Название пространства"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          required
        />
        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Создаём…' : 'Создать'}
        </Button>
      </form>
    </Modal>
  );
}
